/**
 * Shared Firebase Storage utility functions
 * These functions can be used by both client and server code
 */

import { v4 as uuidv4 } from 'uuid';

import type { WebAppEnvironment } from '@/types/api';

import { getFirebaseConfig } from './firebase-config';
/**
 * Creates a unique file name and constructs the storage path
 */
export function createFirebaseStoragePath(fileName: string, path = 'media'): {
  storagePath: string;
  uniqueFileName: string;
} {
  // Create a unique filename to avoid collisions using UUID v4
  const uuid = uuidv4();
  const safeFileName = fileName.replace(/[^a-z0-9.]/gi, '_');
  const uniqueFileName = `${uuid}_${safeFileName}`;

  // Full storage path
  const storagePath = `${path}/${uniqueFileName}`;

  return {
    storagePath,
    uniqueFileName,
  };
}

/**
 * Generates a Firebase Storage upload URL
 */
export function getFirebaseStorageUploadUrl(storagePath: string, targetEnv: WebAppEnvironment): {
  uploadUrl: string;
  storageBucket: string;
} {
  // Get Firebase config from environment
  const firebaseConfig = getFirebaseConfig(targetEnv);
  const storageBucket = firebaseConfig.storageBucket || '';

  // Storage API URL
  const uploadUrl = `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o?name=${encodeURIComponent(storagePath)}`;

  return {
    uploadUrl,
    storageBucket,
  };
}

/**
 * Generates a Firebase Storage download URL
 */
export function getFirebaseStorageDownloadUrl(storagePath: string, downloadToken: string, targetEnv: WebAppEnvironment): string {
  const firebaseConfig = getFirebaseConfig(targetEnv);
  const storageBucket = firebaseConfig.storageBucket || '';

  return `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o/${encodeURIComponent(storagePath)}?alt=media&token=${downloadToken}`;
}

/**
 * Extracts storage path from the upload URL
 */
export function extractStoragePathFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.searchParams.get('name');
    if (path === null) {
      return '';
    }
    return decodeURIComponent(path);
  } catch (error) {
    console.error('Failed to extract storage path from URL:', error);
    return '';
  }
}
